import { Quaternion, Vector3 } from 'three'
import { hash01 } from './iceBurst'
import { UNITY_GRAVITY } from './iceBurst'

/**
 * 📐 The bus's continuous particle systems — exhaust, road trail, depart glow
 * and wind lines — plus the one-shot crash burst.
 *
 * `iceBurst.ts` is the same idea for a single burst off a mesh surface; this is
 * the streaming half, and the two differ in the one way that matters: an ice
 * burst is `rateOverTime: 0` with a single `m_Bursts` entry, while three of the
 * four bus systems emit **per world unit travelled** (`rateOverDistance`) and
 * one emits **per second** (`rateOverTime`). A bus standing still therefore lays
 * down no trail at all, which is most of what makes the trail read as motion.
 *
 * The numbers all live in `BUS_PARTICLE_VFX` / `BUS_CRASH_VFX` — see there for
 * what came from where, and for the `minMaxState` trap that made the first read
 * of them wrong. Nothing in this file draws; `sessionView.ts` does that.
 *
 * As with the ice burst, this is **not** a port of Unity's particle solver —
 * only of the modules these particular systems switch on:
 *
 *  - **Emission**: `rateOverTime` and/or `rateOverDistance`, plus `m_Bursts`.
 *  - **Shape**: Cone (`type: 4`) and Sphere (`type: 0`). `randomDirectionAmount`
 *    is 0 on every one of them, so a particle leaves along the direction its
 *    shape gives it and never wanders.
 *  - **Initial**: lifetime / speed / size / colour ranges and `gravityModifier`.
 *  - **SizeModule** and the alpha track of **ColorModule**, both as piecewise
 *    linear curves over normalised age.
 *
 * Not modelled, because none of these systems uses them in a way that shows:
 * rotation-over-lifetime, velocity-over-lifetime, noise, collision, sub-emitters.
 * The one real omission is `WindlinesSpeedy`'s `TrailModule` — see
 * `BUS_PARTICLE_VFX.windLines`, and the note on `ribbon` below.
 *
 * ⚠️ Determinism (CLAUDE.md §4): the original calls `Random.Range` for every
 * one of these numbers. Nothing here does — each comes out of `hash01`, seeded
 * by the emitter's own id and a per-particle counter, so the same run replays
 * the same smoke. Two emitters that pass the same `seed` will emit in lockstep,
 * which is why the bus id is mixed with a per-system salt at the call site.
 */

/** A `MinMaxCurve` pair as the prefab stores it, `[min, max]`. */
export type Range = readonly [number, number]

/** One key of a piecewise-linear curve over normalised age: `[time, value]`. */
export type CurveKey = readonly [number, number]

export interface ParticleSpec {
  readonly lifetimeSeconds: Range
  readonly speed: Range
  readonly size: Range
  /**
   * `startColor` as a grey range. Every one of these systems is white or a
   * near-white random between two greys — none carries a hue of its own, which
   * is what lets `PlayDepartEffects` tint one of them wholesale.
   */
  readonly colour?: Range
  /** A multiplier on `UNITY_GRAVITY`, **not** an acceleration. Sign matters. */
  readonly gravityModifier: number
  readonly shape: {
    readonly type: 'cone' | 'sphere'
    readonly radius: number
    readonly angle?: number
  }
  readonly sizeCurve: readonly CurveKey[]
  readonly alphaCurve: readonly CurveKey[]
  /**
   * `UVModule` tiling. `1 x 1` means the map is one picture; anything larger is
   * a flipbook sheet and a particle draws **one cell** of it.
   *
   * On these systems `frameOverTime` is a RandomBetweenTwoConstants over the
   * whole range, so the cell is chosen once at birth and held — the module is
   * being used for variety, not for animation.
   */
  readonly sheet?: { readonly tilesX: number; readonly tilesY: number }
  /** `startRotation`, in radians, about the particle's own facing axis. */
  readonly startRotation?: Range
  /**
   * `ClampVelocityModule` — Unity's "Limit Velocity over Lifetime", with
   * `separateAxis: 0` on every system here, so it limits the speed rather than
   * each axis. `null` means the module is off.
   *
   * 🔴 This was listed as "not modelled" on the first pass and it is the module
   * that decides what these effects look like:
   *
   *   - the exhaust leaves at 3-4 u/s and is pulled back to **0.7**, so it hangs
   *     at the tailpipe instead of flying off;
   *   - the crash shards and sparks have `magnitude: 0`, so they are damped to a
   *     **standstill** — a spark leaves at 9-16 u/s and stops within about a
   *     unit. Unmodelled it travels six or more, and the burst reads as a spray
   *     rather than an impact.
   */
  readonly speedLimit?: number | null
  /** The fraction of the excess taken off each frame. 0 disables the clamp. */
  readonly dampen?: number
}

export interface Particle {
  readonly position: Vector3
  readonly velocity: Vector3
  /** Seconds lived. Dead once this passes `lifetime`. */
  age: number
  readonly lifetime: number
  /** `startSize`; the size curve scales this. */
  readonly size: number
  /** `startColor`, as a grey in 0..1. */
  readonly grey: number
  /** Which system in a multi-system effect this belongs to. */
  readonly system: number
  /** Which cell of the flipbook sheet this one draws. 0 when there is no sheet. */
  readonly frame: number
  /** `startRotation` in radians. Constant — no rotation-over-lifetime module. */
  readonly spin: number
  /**
   * A run-time override of `main.startColor`, packed as a three.js hex.
   *
   * 🔍 Only `PlayDepartEffects` sets one:
   *
   *     main.startColor = _color.GetEditorColor();   // alpha forced to 1
   *
   * Carried per **particle** rather than on the emitter or the material because
   * several buses can be departing at once in different colours out of one
   * pool, and because the pool is compacted in place as particles die — a
   * parallel array of tints would drift out of step the first time one died
   * from the middle.
   */
  tint?: number

  /**
   * How far along the road the bus was when this was emitted, or undefined
   * when the emitter was not on the road.
   *
   * 🔍 Emission already stops at a mouth (`SetTrailsActive(false)`, see
   * `advanceRoadTraffic`), but a puff lives up to 1.5 s and a trail 0.25 s, so
   * what was already in the air went on being drawn out in the gap after the
   * bus itself had been cut away. The original never has this problem: the
   * particle systems are parented to the bus and go to the `Invisible` layer
   * with it.
   *
   * Frozen at birth rather than tracked: a puff drifts a few centimetres and
   * lives under a second, and it has no curve of its own to be measured along.
   * Carried per **particle** for the same reason as `tint` — several buses share
   * one pool and it is compacted in place as particles die.
   */
  roadDistance?: number
}

/**
 * Sample a piecewise-linear curve at normalised age `t`.
 *
 * Unity's curves are Hermite splines with real tangents; these are read as
 * straight lines between the keys. 📐 The difference is a few percent of a
 * particle's size or alpha over a lifetime measured in tenths of a second, and
 * modelling the tangents would mean carrying `inSlope`/`outSlope` for every key
 * — cost with nothing visible behind it. Flagged rather than hidden.
 *
 * Outside the key range the curve holds its end value, which is what Unity's
 * default `m_PreInfinity`/`m_PostInfinity` of 2 (ClampForever) does.
 */
export function curveAt(curve: readonly CurveKey[], t: number): number {
  if (curve.length === 0) return 1
  if (t <= curve[0][0]) return curve[0][1]
  const last = curve[curve.length - 1]
  if (t >= last[0]) return last[1]
  for (let i = 1; i < curve.length; i++) {
    const [t1, v1] = curve[i]
    if (t > t1) continue
    const [t0, v0] = curve[i - 1]
    const span = t1 - t0
    return span <= 0 ? v1 : v0 + (v1 - v0) * ((t - t0) / span)
  }
  return last[1]
}

/** The size a particle draws at, right now. */
export const sizeOf = (spec: ParticleSpec, p: Particle): number =>
  p.size * curveAt(spec.sizeCurve, p.age / p.lifetime)

/** The alpha a particle draws at, right now. */
export const alphaOf = (spec: ParticleSpec, p: Particle): number =>
  curveAt(spec.alphaCurve, p.age / p.lifetime)

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

/**
 * Which cell of the sheet a particle draws.
 *
 * `frameOverTime` runs 0..0.9999 over `tilesX * tilesY` cells, so the floor of
 * `frame * tiles` is the index — the 0.9999 is precisely so the last cell is
 * reachable without wrapping to the first.
 */
export function frameOf(spec: ParticleSpec, seed: number, n: number): number {
  const tiles = (spec.sheet?.tilesX ?? 1) * (spec.sheet?.tilesY ?? 1)
  if (tiles <= 1) return 0
  return Math.min(tiles - 1, Math.floor(hash01(seed, n * 7 + 19) * tiles))
}

/** The sub-rectangle of the map cell `frame` occupies: [scaleX, scaleY, offsetX, offsetY]. */
export function frameUv(
  // Only the sheet is read, so the ice-countdown puff's specs — which spell a
  // missing sheet as `null` — qualify too.
  spec: { readonly sheet?: ParticleSpec['sheet'] | null },
  frame: number,
): [number, number, number, number] {
  const tx = spec.sheet?.tilesX ?? 1
  const ty = spec.sheet?.tilesY ?? 1
  if (tx <= 1 && ty <= 1) return [1, 1, 0, 0]
  const col = frame % tx
  // Unity numbers sheet frames left to right, top to bottom, and its V axis runs
  // up — so row 0 is the TOP row, which is the far end of V.
  const row = Math.floor(frame / tx)
  return [1 / tx, 1 / ty, col / tx, 1 - (row + 1) / ty]
}

const _dir = new Vector3()
const _pos = new Vector3()

/**
 * Where a particle is born and which way it leaves, in the emitter's own space.
 *
 * Unity's Sphere shape picks a point in the sphere's volume and sends the
 * particle **outward from the centre through that point**; its Cone shape picks
 * a point on a disc of `radius` and sends the particle out along a direction
 * `angle` degrees off the cone's axis, which for a Unity cone is local **+Z**.
 *
 * `angle: 0` — which `departGlow` uses — therefore collapses the cone to a
 * straight line along +Z, not to a point: the disc still has its radius.
 */
function birth(spec: ParticleSpec, seed: number, n: number): { position: Vector3; direction: Vector3 } {
  const u = hash01(seed, n * 5 + 1)
  const v = hash01(seed, n * 5 + 2)
  const w = hash01(seed, n * 5 + 3)

  if (spec.shape.type === 'sphere') {
    // A direction on the unit sphere, then a radius with the cube root that
    // keeps the volume evenly filled rather than crowding the centre.
    const z = 2 * u - 1
    const phi = 2 * Math.PI * v
    const r = Math.sqrt(Math.max(0, 1 - z * z))
    _dir.set(r * Math.cos(phi), r * Math.sin(phi), z)
    const depth = Math.cbrt(w) * spec.shape.radius
    return { position: _pos.copy(_dir).multiplyScalar(depth).clone(), direction: _dir.clone() }
  }

  // Cone: a point on the base disc, `sqrt` again so the rim is not denser than
  // the middle, and a direction tilted `angle` off +Z towards that point.
  const theta = 2 * Math.PI * u
  const radial = Math.sqrt(v) * spec.shape.radius
  const position = new Vector3(radial * Math.cos(theta), radial * Math.sin(theta), 0)

  const angle = ((spec.shape.angle ?? 0) * Math.PI) / 180
  const spread = Math.tan(angle) * Math.sqrt(w)
  const direction = new Vector3(spread * Math.cos(theta), spread * Math.sin(theta), 1).normalize()
  return { position, direction }
}

/**
 * Emit `count` particles.
 *
 * `origin` and `basis` place the emitter: the shape is built in local space and
 * then rotated and moved, which is what makes a cone point where the bus points.
 * `seed` and `first` together must be unique per particle — pass a counter that
 * only ever goes up, or two puffs from the same bus come out identical.
 */
export function emitParticles(
  spec: ParticleSpec,
  count: number,
  origin: Vector3,
  basis: Quaternion,
  seed: number,
  first: number,
  system = 0,
): Particle[] {
  const out: Particle[] = []
  for (let i = 0; i < count; i++) {
    const n = first + i
    const { position, direction } = birth(spec, seed, n)
    position.applyQuaternion(basis).add(origin)
    direction.applyQuaternion(basis)

    const speed = lerp(spec.speed[0], spec.speed[1], hash01(seed, n * 5 + 4))
    out.push({
      position,
      velocity: direction.multiplyScalar(speed),
      age: 0,
      lifetime: Math.max(
        1e-4,
        lerp(spec.lifetimeSeconds[0], spec.lifetimeSeconds[1], hash01(seed, n * 5 + 5)),
      ),
      size: lerp(spec.size[0], spec.size[1], hash01(seed, n * 7 + 11)),
      grey: spec.colour ? lerp(spec.colour[0], spec.colour[1], hash01(seed, n * 7 + 13)) : 1,
      system,
      frame: frameOf(spec, seed, n),
      spin: spec.startRotation
        ? lerp(spec.startRotation[0], spec.startRotation[1], hash01(seed, n * 7 + 17))
        : 0,
    })
  }
  return out
}

/**
 * Step every particle and drop the dead ones. Returns the survivors, in place.
 *
 * Semi-implicit Euler, same as `advanceIceBurst`: gravity goes on the velocity
 * first and the new velocity moves the particle.
 */
export function advanceParticles(
  particles: Particle[],
  dt: number,
  gravityModifier: number,
  clamp?: { speedLimit?: number | null; dampen?: number },
): Particle[] {
  if (dt <= 0) return particles
  const gravity = UNITY_GRAVITY * gravityModifier * dt

  // `ClampVelocityModule`. Unity applies `dampen` **once per frame**, which
  // makes the result depend on the frame rate; the port normalises it to 60 Hz
  // so a 30 Hz tick and a 60 Hz tick agree. 📐 That is a deliberate difference
  // from the original, and the direction of it is "the same at any frame rate"
  // rather than "the same as Unity at some particular frame rate".
  const limit = clamp?.speedLimit
  const dampen = clamp?.dampen ?? 0
  const keep = limit !== null && limit !== undefined && dampen > 0
    ? Math.pow(1 - dampen, dt * 60)
    : 1

  let write = 0
  for (let i = 0; i < particles.length; i++) {
    const p = particles[i]
    p.age += dt
    if (p.age >= p.lifetime) continue
    if (gravity !== 0) p.velocity.y += gravity

    if (keep < 1) {
      const speed = p.velocity.length()
      if (speed > limit!) {
        // Only the **excess** over the limit is damped, so a particle already
        // under it is left alone — which is why a limit of 0 decays the speed
        // away entirely and a limit of 0.7 leaves a slow drift behind.
        const target = limit! + (speed - limit!) * keep
        p.velocity.multiplyScalar(target / speed)
      }
    }

    p.position.addScaledVector(p.velocity, dt)
    particles[write++] = p
  }
  particles.length = write
  return particles
}

/**
 * The running state of one emitter: how much emission it has banked, and how
 * many particles it has ever produced.
 *
 * Kept as a plain object rather than a class so a view can hold a `Map` of them
 * per bus and drop the lot when the bus goes.
 */
export interface EmitterState {
  /** Fractional particles owed, carried between frames. */
  debt: number
  /** Monotonic counter — the `first` argument to `emitParticles`. */
  emitted: number
  /** Where the emitter was last frame, for `rateOverDistance`. */
  last: Vector3 | null
  /** Whether the system is currently `Play()`ing. */
  playing: boolean
}

export const newEmitter = (): EmitterState => ({
  debt: 0,
  emitted: 0,
  last: null,
  playing: false,
})

/**
 * How many particles this emitter owes for a frame, banking the remainder.
 *
 * ⚠️ **The distance term must not be paid on the frame the emitter starts, nor
 * on the frame it teleports.** `last` is null on the first frame after a
 * `Play()` and after any jump, so the bus does not dump a stripe of smoke over
 * the whole gap it just crossed. This is why `last` is nullable rather than
 * merely initialised to the current position — the same guard has to cover a
 * bus that is repositioned mid-run.
 *
 * `simulationSpeed` scales the time term only; Unity applies it to the whole
 * system clock, but the distance term is already tied to real movement.
 */
export function emissionDue(
  state: EmitterState,
  dt: number,
  position: Vector3,
  rateOverTime: number,
  rateOverDistance: number,
  simulationSpeed = 1,
): number {
  if (!state.playing || dt <= 0) {
    state.last = state.playing ? (state.last ?? position.clone()) : null
    return 0
  }

  state.debt += rateOverTime * dt * simulationSpeed

  if (rateOverDistance > 0) {
    if (state.last !== null) {
      const moved = position.distanceTo(state.last)
      // A jump this large is a teleport, not travel — `dispatch` and the spline
      // hand-off both move a bus further in one frame than it could drive.
      if (moved < TELEPORT_DISTANCE) state.debt += rateOverDistance * moved
    }
    if (state.last === null) state.last = position.clone()
    else state.last.copy(position)
  }

  const whole = Math.floor(state.debt)
  if (whole <= 0) return 0
  state.debt -= whole
  return whole
}

/**
 * 📐 Further than a bus can travel in one frame at any speed the game reaches.
 *
 * `BUS_DISPATCH_SPEED` is 24.64 u/s, and the road speeds are well under it, so
 * even a 100 ms hitch moves a bus about 2.5 units. 8 is comfortably above that
 * and comfortably below the tens of units a hand-off jumps.
 */
const TELEPORT_DISTANCE = 8

/** Start a system. Idempotent, like `ParticleSystem.Play`. */
export function play(state: EmitterState): void {
  state.playing = true
}

/**
 * Stop emitting, keeping what is already alive — `ParticleSystem.Stop` with the
 * default `StopBehavior.StopEmitting`, which is what `TransitionToMainRoad` and
 * `BusCrashVFXManager.OnLevelEnded` both use.
 */
export function stopEmitting(state: EmitterState): void {
  state.playing = false
  state.debt = 0
  state.last = null
}
